import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, createTheme, CssBaseline, Container } from '@mui/material';
import {BrowserRouter as Router, Routes, Route, useNavigate} from 'react-router-dom';
import { LoginButton } from './components/auth/LoginButton';
import {useAuth, useAuthStatus} from './hooks/useAuth';

import { Dashboard } from "./components/dashboard";

// Create Material-UI theme
const theme = createTheme({
  colorSchemes: {
    dark: true,
  },
  palette: {
    primary: {
      main: '#5865F2', // Discord blue
    },
    secondary: {
      main: '#57F287', // Discord green
    },
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Auth callback component
const AuthCallback: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      login(code);
      navigate("/");
    }
  }, [login, navigate]);

  return <div />;
};


// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStatus();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    navigate("/");
  }

  return <>{children}</>;
};

const Auth = () => {
  const { isAuthenticated } = useAuth();

  return <div />
}

function App() {

  return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Container maxWidth="lg">
              <Routes>
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/" element={<Auth />} />
                <Route element={<></>} />
              </Routes>
              <Routes>
                <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                />
                <Route index element={<LoginButton />} />
              </Routes>
            </Container>
          </Router>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
  );
}

export default App;
